import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import type { Finding } from './Findings';
import type { RosterPayload } from './RosterPanel';

export type SyncState =
  | 'in_sync' | 'out_of_sync' | 'never_synced' | 'retrying' | 'not_due' | 'no_score';

export interface StudentReach {
  status: 'reachable' | 'no_course' | 'never_launched' | 'unknown';
  lms_id?: string | null;
  courses?: string[];
  /** Whether Canvas has the assignment itself assigned to them ("Assign To"); null when it cannot say. */
  assigned_in_lms?: boolean | null;
}

export interface StudentSyncRow {
  student_id: number;
  student_name: string;
  state: SyncState;
  reason: string | null;
  /** The LMS's own message for the latest failed post, e.g. "422: The maximum number of allowed attempts…". */
  reason_detail?: string | null;
  current_score: number | null;
  synced_score: number | null;
  synced_at: string | null;
  last_attempt_at: string | null;
  verified: boolean;
  /** A 0 the scorer supplies for missing work on a past-due autogradable assignment — owed, not earned. */
  zero_for_missing_work: boolean;
  lms: StudentReach;
  /** Filled on the page from the payload's launches: their latest open of this assignment from the LMS. */
  opened?: { last_at: string; count: number; course_title: string | null };
}

export interface AssignmentSummary {
  id: number;
  title: string;
  lms_type: string | null;
  staffer: string | null;
  staffer_id: number | null;
  subscriber: string | null;
  due_at: string | null;
  grade_by_assignment: boolean | null;
  linked: boolean;
  courses: string[];
  classroom_count: number;
  student_count: number;
}

/** From LmsGradeSyncLaunchesService: opens of this assignment, and where its classes launch TCI from. */
export interface LaunchesPayload {
  teacher: { at: string; course_id: string | null; course_title: string | null }[];
  students: Record<string, { last_at: string; count: number; course_title: string | null }>;
  classes: {
    id: number;
    name: string | null;
    students: number;
    courses: { course_id: string; course_title: string | null; students: number; last_at: string }[];
  }[];
}

/** From LmsGradeSyncCopiesService: every copy of this content, side by side. */
export interface CopiesPayload {
  copies: {
    id: number;
    title?: string | null;
    current: boolean;
    created_at: string;
    linked: boolean;
    courses: string[];
    /** The course it was last opened from. */
    course_title: string | null;
    /** The LMS courses it posts grades into, titled from launches out of each. */
    linked_courses?: { id: string; title: string | null }[];
    /** Where a never-linked copy was added, from the deep link that created it; certain unless others were close. */
    added_in?: { course_id: string; course_title: string | null; certain: boolean } | null;
    opened_by: { id: number; name: string | null; students: number }[];
    teacher_opens: number;
    delivered: number;
  }[];
  unopened_classes: { id: number; name: string | null }[];
}

export interface AssignmentSyncPayload {
  assignment: AssignmentSummary;
  students: StudentSyncRow[];
  diagnostics: Finding[];
  roster?: RosterPayload;
  launches?: LaunchesPayload;
  copies?: CopiesPayload;
}

const useAssignmentSyncState = (assignmentId: string) => useQuery({
  enabled: assignmentId.trim().length > 0,
  queryFn: async (): Promise<AssignmentSyncPayload> => {
    const { data } = await axios.get(
      Routes.plato_api_sysadmin_grade_sync_assignment_path(assignmentId.trim()),
    );

    return data.data;
  },
  queryKey: ['gradeSyncAssignment', assignmentId],
});

export default useAssignmentSyncState;
